import { useTranslations } from "@/lib/static-intl";
import { useMemo, useState } from "react";
import PDW_ReviewsMediaSection from "./pdw-reviews-media-section";
import Iconify from "@/components/ui/Iconify";
import PDW_ReviewsTilesSection from "./pdw-reviews-tiles-section";
import PDW_TextReviewTile from "./pdw-text-review-tile";
import EmptyState from "@/components/empty-state";

const PDW_PlaceTabReviewsSection = ({
  isCssForDialog,
  place,
  placePosts,
  userFollowing = [],
  placeTextReviews,
  setUserFollowing = () => {},
  handleContentModalOpen,
  openReviewCreateModal,
  openPlaceMediaSlider,
  setPlacePosts,
  setPlaceTextReviews,
  fetchPlacePosts,
  fetchPlaceTextReview,
}) => {
  const t = useTranslations("places.place_details_window");
  const [sort, setSort] = useState("latest");

  // NAVER rating summary card (imported places). Shown only when Naver data exists.
  const naverRatingValue = Number(place?.naverRating);
  const hasNaverRating = Number.isFinite(naverRatingValue) && naverRatingValue > 0;
  const naverVisitorCount = Math.round(Number(place?.numberOfVisitorReviews));
  const hasNaverVisitorCount =
    Number.isFinite(naverVisitorCount) && naverVisitorCount > 0;
  const hasNaverData = hasNaverRating || hasNaverVisitorCount;

  const sortedReviews = useMemo(() => {
    const reviews = [...(placePosts || []), ...(placeTextReviews || [])];
    if (sort === "recommended") {
      const reviewsWithTotalRecs = reviews?.map((_rev) => ({
        ..._rev,
        rec: _rev?._count?.placeReviewRatingLike ?? _rev?._count?.like ?? 0,
      }));
      return [...reviewsWithTotalRecs].sort((a, b) => b?.rec - a?.rec);
    }

    if (sort === "latest") {
      return [...reviews].sort(
        (a, b) => new Date(b?.createdAt) - new Date(a?.createdAt)
      );
    }
  }, [placePosts, placeTextReviews, sort]);

  return (
    <div className="w-full min-h-[80vh] flex flex-col divide-y divide-black/[0.08] dark:divide-white/[0.14] pb-5">
      {hasNaverData && (
        <div className="w-full flex items-center justify-between gap-3 py-4">
          <div className="flex flex-col items-start gap-1.5">
            <span className="text-4xl font-bold leading-none dark:text-white">
              {hasNaverRating ? naverRatingValue.toFixed(1) : "-"}
            </span>
            <div className="flex items-center gap-0.5">
              {[0, 1, 2, 3, 4].map((i) => (
                <Iconify
                  key={i}
                  icon={"ic:round-star"}
                  className={"!text-amber-400 !w-4 !h-4"}
                />
              ))}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <span
              className="rounded-md px-2 py-0.5 text-xs font-bold text-white"
              style={{ backgroundColor: "#03c75a" }}
            >
              NAVER
            </span>
            {hasNaverVisitorCount && (
              <span className="rounded-full border border-black/[0.08] dark:border-white/[0.14] px-3 py-1 text-xs font-medium text-black/70 dark:text-white/70">
                {"방문자 리뷰 "}
                {naverVisitorCount}
              </span>
            )}
          </div>
        </div>
      )}
      {placePosts && placePosts?.length > 0 && (
        <PDW_ReviewsMediaSection
          place={place}
          placePosts={placePosts}
          isCssForDialog={isCssForDialog}
          openPlaceMediaSlider={openPlaceMediaSlider}
          setPlacePosts={setPlacePosts}
        />
      )}
      <div className="w-full flex flex-col gap-3 py-2 dark:text-white">
        <div className="w-full flex items-center justify-between">
          <div className="w-fit flex items-center gap-2">
            <span className="font-semibold text-lg">
              {hasNaverData ? "핫플 리뷰" : t("Reviews")}
            </span>
            <span className="font-bold text-lg text-black/30 dark:text-white/30">
              {sortedReviews.length}
            </span>
          </div>
          <button
            type="button"
            onClick={openReviewCreateModal}
            className="text-sm text-red bg-transparent tracking-tight flex items-center justify-center gap-1 transition"
          >
            <Iconify
              icon={"system-uicons:write"}
              className={"!text-inherit !w-4 !h-5"}
            />
            <span>{t("write_a_review")}</span>
          </button>
        </div>
        {sortedReviews.length > 0 ? (
          <div className="w-full flex flex-col mb-2">
            <div className="w-full flex items-center gap-4">
              <button
                type="button"
                className={`w-fit flex items-center gap-1 text-sm ${
                  sort === "latest"
                    ? "text-black dark:text-white"
                    : "text-black/60 dark:text-white/60"
                }`}
                onClick={() => setSort("latest")}
              >
                <Iconify icon={"bi:dot"} className={"!text-inherit -mx-1.5"} />
                <span>{t("latest")}</span>
              </button>
              <button
                type="button"
                className={`w-fit flex items-center gap-1 text-sm ${
                  sort === "recommended"
                    ? "text-black dark:text-white"
                    : "text-black/60 dark:text-white/60"
                }`}
                onClick={() => setSort("recommended")}
              >
                <Iconify icon={"bi:dot"} className={"!text-inherit -mx-1.5"} />
                <span>{t("recommended")}</span>
              </button>
            </div>
            <div
              className="w-full flex flex-col divide-y divide-black/[0.08] dark:divide-white/[0.14]"
            >
              {sortedReviews.map((review, index) =>
                review?.media ? (
                  <PDW_ReviewsTilesSection
                    key={index}
                    post={review}
                    userFollowing={userFollowing}
                    setUserFollowing={setUserFollowing}
                    handleContentModalOpen={handleContentModalOpen}
                    isCssForDialog={isCssForDialog}
                    setPlaceTextReviews={setPlaceTextReviews}
                    fetchPlacePosts={fetchPlacePosts}
                    fetchPlaceTextReview={fetchPlaceTextReview}
                  />
                ) : (
                  <PDW_TextReviewTile
                    key={review?._id || review?.id || index}
                    _pl={review}
                    setPlacePosts={setPlacePosts}
                    setPlaceTextReviews={setPlaceTextReviews}
                    fetchPlacePosts={fetchPlacePosts}
                    fetchPlaceTextReview={fetchPlaceTextReview}
                  />
                )
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-[300px]">
            <EmptyState
              icon="lucide:message-square-text"
              title={
                hasNaverData ? "아직 핫플 리뷰가 없습니다" : t("no visitor reviews")
              }
              action={{ label: t("write_a_review"), onClick: openReviewCreateModal }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PDW_PlaceTabReviewsSection;
